import type { ContentBlock, Interaction } from "./types";

function interactionKey(interaction: Interaction): string {
  return `${interaction.with.toLowerCase()}::${interaction.concern.toLowerCase()}`;
}

function mergeInteractions(
  explicit: Interaction[] | undefined,
  inferred: Interaction[],
): Interaction[] {
  const merged = [...(explicit ?? [])];
  const seen = new Set(merged.map(interactionKey));

  for (const interaction of inferred) {
    const key = interactionKey(interaction);
    if (seen.has(key)) continue;
    merged.push(interaction);
    seen.add(key);
  }

  return merged;
}

function bleedingRiskInteractions(locale: string): Interaction[] {
  if (locale === "fr") {
    return [
      {
        with: "Aspirine, AINS, anticoagulants ou antiagrégants plaquettaires",
        severity: "critical",
        concern:
          "Combiner plusieurs fluidifiants sanguins peut augmenter les bleus, les saignements prolongés, les saignements de nez, digestifs ou liés à une intervention.",
        recommendation:
          "À utiliser uniquement si ton centre te l'a prescrit, et demande avant d'ajouter ibuprofène, naproxène, oméga-3 à forte dose, vitamine E, curcumine ou resvératrol.",
        linkedBlockId: "med-low-dose-aspirin",
      },
      {
        with: "Ponction ovocytaire, chirurgie, anesthésie péridurale ou rachidienne, ou interventions invasives",
        severity: "caution",
        concern:
          "Les médicaments et compléments fluidifiants sanguins peuvent modifier les saignements péri-opératoires et la planification de l'anesthésie.",
        recommendation:
          "Confirme le calendrier exact d'arrêt et de reprise avec ton centre de fertilité ou ton anesthésiste.",
      },
    ];
  }
  return [
    {
      with: "Aspirin, NSAIDs, anticoagulants, or antiplatelet drugs",
      severity: "critical",
      concern:
        "Combining multiple blood-thinning agents can increase bruising, prolonged bleeding, nosebleeds, GI bleeding, or procedure-related bleeding.",
      recommendation:
        "Use only if your clinic specifically prescribed the combination, and ask before adding ibuprofen, naproxen, high-dose omega-3, vitamin E, curcumin, or resveratrol.",
      linkedBlockId: "med-low-dose-aspirin",
    },
    {
      with: "Egg retrieval, surgery, spinal/epidural anesthesia, or invasive procedures",
      severity: "caution",
      concern:
        "Blood-thinning medicines and supplements can change peri-procedure bleeding and anesthesia planning.",
      recommendation:
        "Confirm the exact stop/restart plan with your fertility clinic or anesthesiologist.",
    },
  ];
}

function mineralBindingInteractions(locale: string): Interaction[] {
  if (locale === "fr") {
    return [
      {
        with: "Lévothyroxine",
        severity: "caution",
        concern:
          "Les minéraux comme le fer, le calcium, le magnésium et le zinc peuvent se lier à la lévothyroxine et réduire l'absorption de l'hormone thyroïdienne.",
        recommendation:
          "Espace de 4 heures au moins par rapport à la lévothyroxine, sauf indication contraire de ta soignante.",
        linkedBlockId: "med-levothyroxine",
      },
      {
        with: "Doxycycline ou autres antibiotiques de la famille des tétracyclines ou fluoroquinolones",
        severity: "caution",
        concern:
          "Le fer, le calcium, le magnésium, le zinc, les produits laitiers et les antiacides peuvent se lier à ces antibiotiques et réduire leur absorption.",
        recommendation:
          "Espace les compléments de minéraux, les produits laitiers et les antiacides de ces antibiotiques selon les indications de ta soignante, souvent d'au moins 2 heures.",
        linkedBlockId: "med-doxycycline",
      },
    ];
  }
  return [
    {
      with: "Levothyroxine",
      severity: "caution",
      concern:
        "Minerals such as iron, calcium, magnesium, and zinc can bind levothyroxine and reduce thyroid hormone absorption.",
      recommendation:
        "Separate from levothyroxine by at least 4 hours unless your clinician gives different instructions.",
      linkedBlockId: "med-levothyroxine",
    },
    {
      with: "Doxycycline or other tetracycline/quinolone antibiotics",
      severity: "caution",
      concern:
        "Iron, calcium, magnesium, zinc, dairy, and antacids can bind these antibiotics and reduce absorption.",
      recommendation:
        "Separate mineral supplements, dairy, and antacids from these antibiotics by the timing your prescriber recommends, often at least 2 hours.",
      linkedBlockId: "med-doxycycline",
    },
  ];
}

function glucoseLoweringInteractions(locale: string): Interaction[] {
  if (locale === "fr") {
    return [
      {
        with: "Metformine, insuline, traitements du diabète ou autres compléments hypoglycémiants",
        severity: "caution",
        concern:
          "Les effets sur la sensibilité à l'insuline ou la glycémie peuvent s'additionner et provoquer des symptômes d'hypoglycémie ou un sur-traitement.",
        recommendation:
          "Préviens ton centre avant de combiner avec metformine, berbérine, acide alpha-lipoïque, inositol ou un traitement du diabète, surtout si tu surveilles ta glycémie.",
        linkedBlockId: "med-metformin",
      },
    ];
  }
  return [
    {
      with: "Metformin, insulin, diabetes medications, or other glucose-lowering supplements",
      severity: "caution",
      concern:
        "Effects on insulin sensitivity or blood glucose can stack and may cause low blood sugar symptoms or medication over-treatment.",
      recommendation:
        "Tell your clinic before combining with metformin, berberine, alpha-lipoic acid, inositol, or diabetes medication, especially if you monitor glucose.",
      linkedBlockId: "med-metformin",
    },
  ];
}

function hormoneProtocolInteractions(locale: string): Interaction[] {
  if (locale === "fr") {
    return [
      {
        with: "Autres médicaments de stimulation ovarienne ou de déclenchement",
        severity: "caution",
        concern:
          "Combiner des hormones de fertilité en dehors du protocole prévu peut augmenter la réponse ovarienne, le risque d'HSO ou le développement de follicules multiples.",
        recommendation:
          "Combine avec les gonadotrophines, le clomifène, le létrozole, l'hCG ou les déclencheurs agonistes de la GnRH uniquement selon le calendrier de ton centre.",
      },
      {
        with: "Compléments à effet hormonal",
        severity: "note",
        concern:
          "Les compléments vendus pour les hormones ou la fertilité peuvent brouiller le suivi de la réponse pendant les cycles monitorés.",
        recommendation:
          "Préviens ton centre si tu prends du gattilier, de la maca, du shatavari, de la DHEA, de l'ashwagandha ou des produits similaires avant de commencer la stimulation.",
      },
    ];
  }
  return [
    {
      with: "Other ovulation-stimulation or trigger medications",
      severity: "caution",
      concern:
        "Combining fertility hormones outside the planned protocol can increase ovarian response, OHSS risk, or multiple-follicle development.",
      recommendation:
        "Only combine with gonadotropins, clomiphene, letrozole, hCG, or GnRH agonist triggers exactly as your clinic schedules them.",
    },
    {
      with: "Hormone-active supplements",
      severity: "note",
      concern:
        "Supplements marketed for hormones or fertility may blur response tracking during monitored cycles.",
      recommendation:
        "Tell your clinic about vitex, maca, shatavari, DHEA, ashwagandha, or similar products before starting stimulation.",
    },
  ];
}

function estrogenInteractions(locale: string): Interaction[] {
  if (locale === "fr") {
    return [
      {
        with: "Médicaments inducteurs enzymatiques ou millepertuis",
        severity: "caution",
        concern:
          "Certains antiépileptiques, antibiotiques de la famille de la rifampicine et le millepertuis peuvent réduire l'exposition aux œstrogènes.",
        recommendation:
          "Préviens ton centre avant de t'appuyer sur les œstrogènes pour le contrôle du cycle, le soutien de l'endomètre ou la suppression.",
      },
      {
        with: "Lévothyroxine",
        severity: "note",
        concern:
          "Les œstrogènes peuvent augmenter les protéines liant la thyroïde, ce qui peut modifier les besoins en hormone thyroïdienne chez certaines patientes.",
        recommendation:
          "Garde tes contrôles de TSH prévus et préviens ton centre si ton protocole d'œstrogènes change.",
        linkedBlockId: "med-levothyroxine",
      },
      {
        with: "Anticoagulants ou conditions à risque de caillots",
        severity: "caution",
        concern:
          "Les protocoles contenant des œstrogènes peuvent influencer le risque de caillots et modifier la balance bénéfice-risque pour les anticoagulants.",
        recommendation:
          "Assure-toi que ton centre est au courant de tout antécédent de thrombose, thrombophilie, ou prise d'aspirine, HBPM ou autre anticoagulant.",
      },
    ];
  }
  return [
    {
      with: "Enzyme-inducing medicines or St. John's wort",
      severity: "caution",
      concern:
        "Some anti-seizure medicines, rifampin-like antibiotics, and St. John's wort can reduce estrogen exposure.",
      recommendation:
        "Tell your clinic about these before relying on estrogen for cycle control, lining support, or suppression.",
    },
    {
      with: "Levothyroxine",
      severity: "note",
      concern:
        "Estrogen can increase thyroid-binding proteins, which may change thyroid hormone dose needs in some patients.",
      recommendation:
        "Keep planned TSH checks and tell your clinic if your estrogen protocol changes.",
      linkedBlockId: "med-levothyroxine",
    },
    {
      with: "Anticoagulants or clotting-risk conditions",
      severity: "caution",
      concern:
        "Estrogen-containing protocols can affect clotting risk and may change the risk/benefit discussion for blood thinners.",
      recommendation:
        "Make sure your clinic knows about clot history, thrombophilia, aspirin, LMWH, or other anticoagulant use.",
    },
  ];
}

function progesteroneInteractions(locale: string): Interaction[] {
  if (locale === "fr") {
    return [
      {
        with: "Médicaments inducteurs enzymatiques ou millepertuis",
        severity: "caution",
        concern:
          "Certains antiépileptiques, antibiotiques de la famille de la rifampicine et le millepertuis peuvent diminuer l'exposition aux progestatifs.",
        recommendation:
          "Préviens ton centre avant de combiner, pour que le soutien lutéal ou la dose de contrôle du cycle puisse être réévalué.",
      },
      {
        with: "Sédatifs, alcool ou médicaments anxiolytiques",
        severity: "note",
        concern:
          "Certains produits à base de progestérone peuvent causer des vertiges ou de la somnolence, qui peuvent s'additionner à d'autres sédatifs.",
        recommendation:
          "Sois prudente au volant et demande avant de combiner avec du diazépam, des somnifères, des opioïdes ou de l'alcool.",
      },
    ];
  }
  return [
    {
      with: "Enzyme-inducing medicines or St. John's wort",
      severity: "caution",
      concern:
        "Some anti-seizure medicines, rifampin-like antibiotics, and St. John's wort can lower progestogen exposure.",
      recommendation:
        "Tell your clinic before combining so luteal support or cycle-control dosing can be reviewed.",
    },
    {
      with: "Sedatives, alcohol, or anti-anxiety medication",
      severity: "note",
      concern:
        "Some progesterone products can cause dizziness or sleepiness, which can stack with other sedating agents.",
      recommendation:
        "Use caution with driving and ask before combining with diazepam, sleep aids, opioids, or alcohol.",
    },
  ];
}

function inferMedicationInteractions(
  block: ContentBlock<"medication">,
  locale: string,
): Interaction[] {
  const id = block.id.toString();
  const body = block.body;
  const drugClass = body.drugClass.toLowerCase();
  const title = block.title.toLowerCase();
  const generic = body.genericName.toLowerCase();
  const interactions: Interaction[] = [];
  const fr = locale === "fr";

  if (drugClass.includes("anticoagulant")) {
    interactions.push(...bleedingRiskInteractions(locale));
  }

  if (id === "med-low-dose-aspirin") {
    interactions.push(...bleedingRiskInteractions(locale));
  }

  if (drugClass.includes("dopamine agonist")) {
    interactions.push(
      fr
        ? {
            with: "Médicaments bloqueurs de la dopamine comme le métoclopramide ou les antipsychotiques",
            severity: "critical",
            concern:
              "Les antagonistes de la dopamine peuvent agir contre la cabergoline ou la bromocriptine et réduire leur efficacité ou aggraver les effets secondaires.",
            recommendation:
              "Ne combine pas sans que la soignante prescriptrice ait revu le plan.",
          }
        : {
            with: "Dopamine-blocking medicines such as metoclopramide or antipsychotics",
            severity: "critical",
            concern:
              "Dopamine antagonists can work against cabergoline or bromocriptine and may reduce effectiveness or worsen side effects.",
            recommendation:
              "Do not combine without the prescribing clinician reviewing the plan.",
          },
      fr
        ? {
            with: "Antihypertenseurs, alcool ou autres médicaments donnant des vertiges",
            severity: "caution",
            concern:
              "Les agonistes dopaminergiques peuvent abaisser la tension artérielle et provoquer des vertiges, surtout après les premières doses.",
            recommendation:
              "Prends comme indiqué, lève-toi doucement, et préviens ton centre si tu prends des antihypertenseurs ou des sédatifs.",
          }
        : {
            with: "Blood-pressure medication, alcohol, or other dizziness-causing medicines",
            severity: "caution",
            concern:
              "Dopamine agonists can lower blood pressure and cause dizziness, especially after the first doses.",
            recommendation:
              "Take as directed, rise slowly, and tell your clinic if you use antihypertensives or sedatives.",
          },
    );
  }

  if (id === "med-levothyroxine") {
    interactions.push(
      ...mineralBindingInteractions(locale),
      fr
        ? {
            with: "Café, antiacides, chélateurs des acides biliaires ou anti-acides",
            severity: "caution",
            concern:
              "Les aliments, le café et plusieurs médicaments digestifs peuvent réduire ou déstabiliser l'absorption de la lévothyroxine.",
            recommendation:
              "Prends-la régulièrement à jeun avec de l'eau et garde la même routine quand tu contrôles ta TSH.",
          }
        : {
            with: "Coffee, antacids, bile-acid binders, or acid-reducing medicines",
            severity: "caution",
            concern:
              "Food, coffee, and several GI medicines can reduce or destabilize levothyroxine absorption.",
            recommendation:
              "Take consistently on an empty stomach with water and keep the same routine when checking TSH.",
          },
      fr
        ? {
            with: "Médicaments contenant des œstrogènes",
            severity: "note",
            concern:
              "Les œstrogènes peuvent modifier la liaison de l'hormone thyroïdienne et changer les besoins de dose.",
            recommendation:
              "Refais une TSH selon le calendrier de ton centre après un changement majeur de protocole d'œstrogènes.",
            linkedBlockId: "med-combined-oral-contraceptive",
          }
        : {
            with: "Estrogen-containing medications",
            severity: "note",
            concern:
              "Estrogen can change thyroid hormone binding and may alter dose needs.",
            recommendation:
              "Recheck TSH on your clinic's schedule after major estrogen-protocol changes.",
            linkedBlockId: "med-combined-oral-contraceptive",
          },
    );
  }

  if (id === "med-doxycycline") {
    interactions.push(
      fr
        ? {
            with: "Fer, calcium, magnésium, zinc, produits laitiers, antiacides ou vitamines prénatales",
            severity: "caution",
            concern:
              "Les minéraux se lient à la doxycycline et peuvent rendre l'antibiotique moins efficace.",
            recommendation:
              "Espace-les de la doxycycline selon les indications de ta soignante, souvent d'au moins 2 heures.",
            linkedBlockId: "supplement-prenatal",
          }
        : {
            with: "Iron, calcium, magnesium, zinc, dairy, antacids, or prenatal vitamins",
            severity: "caution",
            concern:
              "Minerals bind doxycycline and can make the antibiotic less effective.",
            recommendation:
              "Separate these from doxycycline by the timing your prescriber recommends, often at least 2 hours.",
            linkedBlockId: "supplement-prenatal",
          },
      fr
        ? {
            with: "Isotrétinoïne ou autres rétinoïdes",
            severity: "critical",
            concern:
              "Combiner les tétracyclines avec les rétinoïdes peut augmenter le risque d'hypertension intracrânienne.",
            recommendation:
              "Préviens ta soignante avant de combiner; les rétinoïdes sont aussi incompatibles avec une tentative de grossesse.",
          }
        : {
            with: "Isotretinoin or other retinoids",
            severity: "critical",
            concern:
              "Combining tetracyclines with retinoids can increase intracranial pressure risk.",
            recommendation:
              "Tell your prescriber before combining; retinoids are also not compatible with pregnancy attempts.",
          },
      fr
        ? {
            with: "Warfarine ou autres anticoagulants",
            severity: "caution",
            concern:
              "Les antibiotiques peuvent modifier l'effet anticoagulant ou le risque de saignement chez certaines patientes.",
            recommendation:
              "Demande à la soignante prescriptrice si une surveillance ou un ajustement temporaire est nécessaire.",
            linkedBlockId: "med-warfarin",
          }
        : {
            with: "Warfarin or other anticoagulants",
            severity: "caution",
            concern:
              "Antibiotics can change anticoagulant effect or bleeding risk in some patients.",
            recommendation:
              "Ask the prescribing clinician whether monitoring or temporary adjustment is needed.",
            linkedBlockId: "med-warfarin",
          },
    );
  }

  if (drugClass.includes("corticosteroid")) {
    interactions.push(
      fr
        ? {
            with: "AINS, aspirine ou anticoagulants",
            severity: "caution",
            concern:
              "Les corticoïdes peuvent augmenter l'irritation de l'estomac ou le risque de saignement en association avec les AINS ou les anticoagulants.",
            recommendation:
              "Utilise uniquement comme indiqué et demande avant d'ajouter ibuprofène, naproxène ou de l'aspirine supplémentaire.",
            linkedBlockId: "med-low-dose-aspirin",
          }
        : {
            with: "NSAIDs, aspirin, or anticoagulants",
            severity: "caution",
            concern:
              "Steroids can increase stomach irritation or bleeding risk when combined with NSAIDs or blood thinners.",
            recommendation:
              "Use only as directed and ask before adding ibuprofen, naproxen, or extra aspirin.",
            linkedBlockId: "med-low-dose-aspirin",
          },
      fr
        ? {
            with: "Traitements du diabète ou compléments hypoglycémiants",
            severity: "caution",
            concern:
              "Les corticoïdes peuvent élever la glycémie et contrer les traitements hypoglycémiants.",
            recommendation:
              "Préviens ton centre si tu prends metformine, insuline, berbérine, acide alpha-lipoïque ou inositol.",
            linkedBlockId: "med-metformin",
          }
        : {
            with: "Diabetes medicines or glucose-lowering supplements",
            severity: "caution",
            concern:
              "Steroids can raise blood glucose and may work against glucose-lowering treatments.",
            recommendation:
              "Tell your clinic if you take metformin, insulin, berberine, alpha-lipoic acid, or inositol.",
            linkedBlockId: "med-metformin",
          },
      fr
        ? {
            with: "Vaccins vivants ou autres médicaments immunosuppresseurs",
            severity: "caution",
            concern:
              "L'immunosuppression peut s'additionner, surtout avec des cures prolongées ou à doses élevées.",
            recommendation:
              "Vérifie avec ta soignante avant les vaccins ou avant de combiner avec d'autres traitements immunitaires.",
          }
        : {
            with: "Live vaccines or other immune-suppressing medicines",
            severity: "caution",
            concern:
              "Immune suppression can stack, especially with longer courses or higher doses.",
            recommendation:
              "Check with your clinician before vaccines or combining with other immune therapies.",
          },
    );
  }

  if (id === "med-metformin") {
    interactions.push(
      ...glucoseLoweringInteractions(locale),
      fr
        ? {
            with: "Alcool ou situations de déshydratation/risque rénal",
            severity: "caution",
            concern:
              "Une consommation d'alcool importante, la déshydratation, une insuffisance rénale ou une maladie aiguë peuvent augmenter le risque lié à la metformine.",
            recommendation:
              "Renseigne-toi sur les règles à suivre en cas de maladie et préviens ta soignante avant une intervention, une imagerie avec produit de contraste ou une gastro-entérite sévère.",
          }
        : {
            with: "Alcohol or dehydration/renal-risk situations",
            severity: "caution",
            concern:
              "Heavy alcohol use, dehydration, kidney impairment, or acute illness can increase metformin safety risk.",
            recommendation:
              "Ask about sick-day rules and tell your clinician before procedures, contrast imaging, or severe GI illness.",
          },
    );
  }

  if (id === "med-diazepam") {
    interactions.push(
      fr
        ? {
            with: "Alcool, opioïdes, somnifères, antihistaminiques ou autres sédatifs",
            severity: "critical",
            concern:
              "Le risque de sédation et de ralentissement respiratoire peut s'additionner avec le diazépam.",
            recommendation:
              "Ne combine pas sans indication précise; prévois un transport si tu en prends autour d'une intervention.",
          }
        : {
            with: "Alcohol, opioids, sleep medicines, antihistamines, or other sedatives",
            severity: "critical",
            concern:
              "Sedation and slowed breathing risk can stack with diazepam.",
            recommendation:
              "Do not combine unless specifically directed; arrange transport if using around procedures.",
          },
    );
  }

  if (id === "med-sildenafil") {
    interactions.push(
      fr
        ? {
            with: "Dérivés nitrés ou riociguat",
            severity: "critical",
            concern:
              "L'association peut provoquer une chute dangereuse de la tension artérielle.",
            recommendation:
              "Ne combine pas; assure-toi que toutes les soignantes prescriptrices savent que tu prends du sildénafil.",
          }
        : {
            with: "Nitrates or riociguat",
            severity: "critical",
            concern:
              "The combination can cause a dangerous drop in blood pressure.",
            recommendation:
              "Do not combine; make sure all prescribers know about sildenafil use.",
          },
      fr
        ? {
            with: "Alpha-bloquants, antihypertenseurs ou inhibiteurs puissants du CYP3A4",
            severity: "caution",
            concern:
              "Ces médicaments peuvent augmenter les vertiges, l'hypotension ou l'exposition au sildénafil.",
            recommendation:
              "Demande à ta soignante ou à ta pharmacienne avant de combiner.",
          }
        : {
            with: "Alpha-blockers, blood-pressure medicines, or strong CYP3A4 inhibitors",
            severity: "caution",
            concern:
              "These can increase dizziness, low blood pressure, or sildenafil exposure.",
            recommendation:
              "Ask your clinician or pharmacist before combining.",
          },
    );
  }

  if (drugClass.includes("thyroid")) {
    interactions.push(...mineralBindingInteractions(locale));
  }

  if (drugClass.includes("estrogen") || title.includes("contraceptive")) {
    interactions.push(...estrogenInteractions(locale));
  }

  if (drugClass.includes("progest")) {
    interactions.push(...progesteroneInteractions(locale));
  }

  if (
    drugClass.includes("gonadotropin") ||
    drugClass.includes("chorionic gonadotropin") ||
    drugClass.includes("gnrh") ||
    drugClass.includes("serm") ||
    drugClass.includes("aromatase")
  ) {
    interactions.push(...hormoneProtocolInteractions(locale));
  }

  if (drugClass.includes("aromatase")) {
    interactions.push(
      fr
        ? {
            with: "Médicaments ou compléments contenant des œstrogènes",
            severity: "caution",
            concern:
              "Les produits à base d'œstrogènes peuvent agir contre l'effet anti-œstrogénique recherché des inhibiteurs de l'aromatase.",
            recommendation:
              "Utilise uniquement dans le cadre du protocole du centre et signale crèmes, comprimés, patchs d'œstrogènes ou compléments hormonaux.",
          }
        : {
            with: "Estrogen-containing medicines or supplements",
            severity: "caution",
            concern:
              "Estrogen products can work against the intended estrogen-lowering effect of aromatase inhibitors.",
            recommendation:
              "Use only within the clinic protocol and disclose estrogen creams, pills, patches, or hormone supplements.",
          },
    );
  }

  if (drugClass.includes("serm") || generic.includes("tamoxifen")) {
    interactions.push(
      fr
        ? {
            with: "Médicaments contenant des œstrogènes ou autres médicaments d'induction de l'ovulation",
            severity: "caution",
            concern:
              "Les SERM font partie d'un protocole hormonal; des œstrogènes ou des médicaments de stimulation non prévus peuvent modifier la réponse et le suivi.",
            recommendation:
              "Combine avec les œstrogènes, les gonadotrophines ou les injections de déclenchement uniquement selon le calendrier de ton centre.",
          }
        : {
            with: "Estrogen-containing medicines or other ovulation-induction drugs",
            severity: "caution",
            concern:
              "SERMs are part of a hormone protocol; unscheduled estrogen or stimulation medicines can change response and monitoring.",
            recommendation:
              "Only combine with estrogen, gonadotropins, or trigger shots on your clinic's schedule.",
          },
    );
  }

  if (generic.includes("tamoxifen")) {
    interactions.push(
      fr
        ? {
            with: "Warfarine ou autres anticoagulants",
            severity: "critical",
            concern:
              "Le tamoxifène peut augmenter l'effet anticoagulant et le risque de saignement.",
            recommendation:
              "Ne combine pas sans surveillance et suivi par la prescriptrice.",
            linkedBlockId: "med-warfarin",
          }
        : {
            with: "Warfarin or other anticoagulants",
            severity: "critical",
            concern:
              "Tamoxifen can increase anticoagulant effect and bleeding risk.",
            recommendation:
              "Do not combine without prescriber oversight and monitoring.",
            linkedBlockId: "med-warfarin",
          },
      fr
        ? {
            with: "Antidépresseurs inhibiteurs puissants du CYP2D6",
            severity: "caution",
            concern:
              "Certains antidépresseurs peuvent réduire la conversion du tamoxifène en ses métabolites actifs.",
            recommendation:
              "Demande à la prescriptrice avant de combiner avec paroxétine, fluoxétine, bupropion ou des médicaments similaires.",
          }
        : {
            with: "Strong CYP2D6-inhibiting antidepressants",
            severity: "caution",
            concern:
              "Some antidepressants can reduce conversion to active tamoxifen metabolites.",
            recommendation:
              "Ask the prescriber before combining with paroxetine, fluoxetine, bupropion, or similar medicines.",
          },
    );
  }

  if (id === "med-hydroxychloroquine") {
    interactions.push(
      fr
        ? {
            with: "Médicaments allongeant le QT",
            severity: "caution",
            concern:
              "L'hydroxychloroquine peut contribuer au risque de trouble du rythme cardiaque en association avec d'autres médicaments allongeant le QT.",
            recommendation:
              "Préviens ta soignante si tu prends des antibiotiques macrolides, des antiarythmiques, des antipsychotiques ou d'autres médicaments à risque pour le QT.",
          }
        : {
            with: "QT-prolonging medicines",
            severity: "caution",
            concern:
              "Hydroxychloroquine can contribute to heart-rhythm risk when combined with other QT-prolonging drugs.",
            recommendation:
              "Tell your clinician about macrolide antibiotics, antiarrhythmics, antipsychotics, or other QT-risk medicines.",
          },
      ...glucoseLoweringInteractions(locale),
    );
  }

  if (id === "med-filgrastim") {
    interactions.push(
      fr
        ? {
            with: "Lithium ou autres traitements stimulant les globules blancs",
            severity: "caution",
            concern:
              "Les effets sur les globules blancs peuvent s'additionner et compliquer le suivi.",
            recommendation:
              "Assure-toi que ton centre est au courant si tu prends du lithium ou des traitements hématologiques.",
          }
        : {
            with: "Lithium or other white-blood-cell stimulating treatments",
            severity: "caution",
            concern:
              "White blood cell effects can stack and may complicate monitoring.",
            recommendation:
              "Make sure your clinic knows about lithium or hematology-directed treatments.",
          },
    );
  }

  if (id === "med-ivig") {
    interactions.push(
      fr
        ? {
            with: "Vaccins vivants",
            severity: "caution",
            concern:
              "Les IgIV peuvent réduire la réponse à certains vaccins vivants pendant une période après la perfusion.",
            recommendation: "Revois le calendrier vaccinal avec ta soignante.",
          }
        : {
            with: "Live vaccines",
            severity: "caution",
            concern:
              "IVIG can reduce response to some live vaccines for a period after infusion.",
            recommendation: "Review vaccine timing with your clinician.",
          },
      fr
        ? {
            with: "Diurétiques, médicaments à risque rénal ou médicaments à risque de caillots",
            severity: "caution",
            concern:
              "Les IgIV peuvent rarement affecter la fonction rénale ou le risque de caillots, surtout chez les patientes à risque.",
            recommendation:
              "Préviens l'équipe de perfusion en cas de maladie rénale, antécédent de caillots, traitement par œstrogènes, anticoagulants ou diurétiques.",
          }
        : {
            with: "Diuretics, kidney-risk medicines, or clot-risk medicines",
            severity: "caution",
            concern:
              "IVIG can rarely affect kidney function or clotting risk, especially in higher-risk patients.",
            recommendation:
              "Tell the infusion team about kidney disease, clot history, estrogen therapy, anticoagulants, or diuretics.",
          },
    );
  }

  if (id === "med-intralipid") {
    interactions.push(
      fr
        ? {
            with: "Autres nutritions lipidiques IV ou produits contenant œuf ou soja",
            severity: "caution",
            concern:
              "Le doublement des perfusions lipidiques et le risque allergique sont à prendre en compte avec les émulsions lipidiques IV.",
            recommendation:
              "Préviens ton centre en cas de réaction aux œufs, au soja, aux arachides ou aux émulsions lipidiques avant la perfusion.",
          }
        : {
            with: "Other IV lipid nutrition or egg/soy-containing products",
            severity: "caution",
            concern:
              "Duplicate lipid infusions and allergy risk can matter with IV lipid emulsions.",
            recommendation:
              "Tell your clinic about egg, soy, peanut, or lipid-emulsion reactions before infusion.",
          },
    );
  }

  if (id === "med-growth-hormone") {
    interactions.push(
      fr
        ? {
            with: "Insuline, metformine, traitements du diabète ou compléments hypoglycémiants",
            severity: "caution",
            concern:
              "L'hormone de croissance peut influencer la sensibilité à l'insuline et la glycémie.",
            recommendation:
              "Préviens ton centre si tu prends des hypoglycémiants ou si tu as un diabète ou une insulino-résistance.",
            linkedBlockId: "med-metformin",
          }
        : {
            with: "Insulin, metformin, diabetes medicines, or glucose-lowering supplements",
            severity: "caution",
            concern:
              "Growth hormone can affect insulin sensitivity and glucose levels.",
            recommendation:
              "Tell your clinic if you use glucose-lowering medicines or have diabetes/insulin resistance.",
            linkedBlockId: "med-metformin",
          },
      fr
        ? {
            with: "Corticoïdes ou œstrogènes par voie orale",
            severity: "note",
            concern:
              "Les corticoïdes et les œstrogènes oraux peuvent modifier la réponse à l'hormone de croissance ou à l'IGF-1.",
            recommendation:
              "Utilise uniquement dans le cadre du protocole monitoré du centre.",
          }
        : {
            with: "Corticosteroids or oral estrogen",
            severity: "note",
            concern:
              "Steroids and oral estrogen can change growth-hormone or IGF-1 response.",
            recommendation: "Use only within the clinic's monitored protocol.",
          },
    );
  }

  if (drugClass.includes("glp-1") || drugClass.includes("gip/glp-1")) {
    interactions.push(
      fr
        ? {
            with: "Médicaments oraux dont l'absorption dépend du timing",
            severity: "caution",
            concern:
              "Les GLP-1 ralentissent la vidange gastrique et peuvent modifier la vitesse d'absorption de certains médicaments oraux.",
            recommendation:
              "Préviens ton centre si tu prends de la lévothyroxine, des antibiotiques, des hormones par voie orale ou tout médicament dont le timing est important.",
            linkedBlockId: "med-levothyroxine",
          }
        : {
            with: "Oral medications with timing-sensitive absorption",
            severity: "caution",
            concern:
              "GLP-1 medicines delay gastric emptying and can change how quickly some oral medications are absorbed.",
            recommendation:
              "Tell your clinic about levothyroxine, antibiotics, oral hormones, and any medication where timing matters.",
            linkedBlockId: "med-levothyroxine",
          },
      fr
        ? {
            with: "Metformine, insuline, traitements du diabète ou compléments hypoglycémiants",
            severity: "caution",
            concern:
              "Les effets hypoglycémiants peuvent s'additionner, surtout lors de l'augmentation de dose, d'une baisse des apports, de nausées ou de vomissements.",
            recommendation:
              "Demande à la prescriptrice si une surveillance de la glycémie ou un changement de dose est nécessaire.",
            linkedBlockId: "med-metformin",
          }
        : {
            with: "Metformin, insulin, diabetes medicines, or glucose-lowering supplements",
            severity: "caution",
            concern:
              "Glucose-lowering effects can stack, especially during dose escalation, reduced intake, nausea, or vomiting.",
            recommendation:
              "Ask the prescribing clinician whether glucose monitoring or dose changes are needed.",
            linkedBlockId: "med-metformin",
          },
      fr
        ? {
            with: "Pilules contraceptives / contraception hormonale orale",
            severity: "caution",
            concern:
              id === "med-tirzepatide"
                ? "Le tirzépatide peut réduire l'efficacité des contraceptifs oraux au démarrage et lors de l'augmentation de dose à cause du ralentissement de la vidange gastrique."
                : "Les nausées, vomissements, diarrhée ou le ralentissement de la vidange gastrique liés aux GLP-1 peuvent rendre la pilule moins fiable en pratique, même quand l'interaction directe n'est pas établie.",
            recommendation:
              id === "med-tirzepatide"
                ? "Utilise une contraception non orale ou ajoute une contraception barrière pendant la période indiquée par la notice après le démarrage ou l'augmentation de dose."
                : "Préviens ta soignante si tu comptes sur la pilule; demande si une protection complémentaire ou une contraception non orale est plus sûre pendant l'augmentation de dose ou en cas de symptômes digestifs marqués.",
            linkedBlockId: "med-combined-oral-contraceptive",
          }
        : {
            with: "Birth control pills / oral hormonal contraception",
            severity: "caution",
            concern:
              id === "med-tirzepatide"
                ? "Tirzepatide can reduce oral contraceptive effectiveness around initiation and dose escalation due to delayed gastric emptying."
                : "GLP-1 nausea, vomiting, diarrhea, or delayed gastric emptying can make oral birth control less reliable in practice, even when a direct label interaction is not established.",
            recommendation:
              id === "med-tirzepatide"
                ? "Use non-oral contraception or add barrier contraception for the label-specified window after starting or increasing the dose."
                : "Tell your clinician if you rely on birth control pills; ask whether backup or non-oral contraception is safer during dose escalation or significant GI symptoms.",
            linkedBlockId: "med-combined-oral-contraceptive",
          },
      fr
        ? {
            with: "Anesthésie, ponction ovocytaire ou interventions avec sédation",
            severity: "caution",
            concern:
              "Le ralentissement de la vidange gastrique et les nausées/vomissements peuvent affecter le jeûne préopératoire et la planification de l'anesthésie.",
            recommendation:
              "Préviens ton centre et l'équipe d'anesthésie que tu prends un GLP-1 avant la ponction ou une intervention.",
          }
        : {
            with: "Anesthesia, egg retrieval, or procedures with sedation",
            severity: "caution",
            concern:
              "Delayed gastric emptying and nausea/vomiting can affect pre-procedure fasting and anesthesia planning.",
            recommendation:
              "Tell your clinic and anesthesiology team you are using a GLP-1 medication before retrieval or surgery.",
          },
    );
  }

  return interactions;
}

function inferSupplementInteractions(
  block: ContentBlock<"supplement">,
  locale: string,
): Interaction[] {
  const id = block.id.toString();
  const title = block.title.toLowerCase();
  const interactions: Interaction[] = [];
  const fr = locale === "fr";

  if (
    [
      "supplement-alpha-lipoic-acid",
      "supplement-berberine",
      "supplement-myo-inositol",
      "supplement-shatavari",
    ].includes(id)
  ) {
    interactions.push(...glucoseLoweringInteractions(locale));
  }

  if (
    [
      "supplement-omega-3",
      "supplement-vitamin-e",
      "supplement-curcumin",
      "supplement-resveratrol",
      "supplement-nac",
      "supplement-royal-jelly",
    ].includes(id)
  ) {
    interactions.push(
      fr
        ? {
            with: "Aspirine, HBPM, anticoagulants, antiagrégants plaquettaires ou interventions",
            severity: "caution",
            concern:
              "Ce complément peut influencer la tendance aux saignements, la fonction plaquettaire ou la planification d'une intervention, surtout à fortes doses.",
            recommendation:
              "Préviens ton centre avant une ponction ovocytaire, une chirurgie, un protocole de transfert avec aspirine/HBPM, ou si tu te fais des bleus ou saignes facilement.",
            linkedBlockId: "med-low-dose-aspirin",
          }
        : {
            with: "Aspirin, LMWH, anticoagulants, antiplatelet drugs, or procedures",
            severity: "caution",
            concern:
              "This supplement may affect bleeding tendency, platelet function, or procedure planning, especially at higher doses.",
            recommendation:
              "Tell your clinic before egg retrieval, surgery, transfer protocols using aspirin/LMWH, or if you bruise/bleed easily.",
            linkedBlockId: "med-low-dose-aspirin",
          },
    );
  }

  if (
    [
      "supplement-iron",
      "supplement-calcium",
      "supplement-magnesium",
      "supplement-zinc",
    ].includes(id)
  ) {
    interactions.push(...mineralBindingInteractions(locale));
  }

  if (id === "supplement-coq10") {
    interactions.push(
      fr
        ? {
            with: "Warfarine ou autres anticoagulants",
            severity: "caution",
            concern:
              "Le CoQ10 peut interférer avec le contrôle de l'anticoagulation chez certaines patientes.",
            recommendation:
              "Préviens la prescriptrice de l'anticoagulation avant de commencer ou de changer la dose.",
            linkedBlockId: "med-warfarin",
          }
        : {
            with: "Warfarin or other anticoagulants",
            severity: "caution",
            concern:
              "CoQ10 may interfere with anticoagulation control in some patients.",
            recommendation:
              "Tell the anticoagulation prescriber before starting or changing the dose.",
            linkedBlockId: "med-warfarin",
          },
      fr
        ? {
            with: "Antihypertenseurs",
            severity: "note",
            concern:
              "Le CoQ10 peut légèrement influencer la tension artérielle chez certaines personnes.",
            recommendation:
              "Mentionne-le si tu surveilles ta tension ou si tu prends des antihypertenseurs.",
          }
        : {
            with: "Blood-pressure medication",
            severity: "note",
            concern: "CoQ10 can modestly affect blood pressure in some people.",
            recommendation:
              "Mention it if you monitor blood pressure or take antihypertensives.",
          },
    );
  }

  if (id === "supplement-vitamin-d") {
    interactions.push(
      fr
        ? {
            with: "Diurétiques thiazidiques, digoxine, calcium à forte dose ou maladie rénale",
            severity: "caution",
            concern:
              "Une exposition élevée à la vitamine D peut contribuer à un risque d'hypercalcémie chez les patientes prédisposées.",
            recommendation:
              "Évite de cumuler vitamine D à forte dose et calcium sans surveillance des taux sanguins.",
            linkedBlockId: "supplement-calcium",
          }
        : {
            with: "Thiazide diuretics, digoxin, high-dose calcium, or kidney disease",
            severity: "caution",
            concern:
              "High vitamin D exposure can contribute to high calcium risk in susceptible patients.",
            recommendation:
              "Avoid stacking high-dose vitamin D and calcium without blood-level monitoring.",
            linkedBlockId: "supplement-calcium",
          },
      fr
        ? {
            with: "Médicaments amaigrissants, chélateurs des acides biliaires ou antiépileptiques",
            severity: "note",
            concern:
              "Certains médicaments peuvent réduire l'absorption de la vitamine D ou en modifier le métabolisme.",
            recommendation:
              "Demande si ta dose ou ta surveillance sanguine doit être adaptée.",
          }
        : {
            with: "Weight-loss drugs, bile-acid binders, or seizure medicines",
            severity: "note",
            concern:
              "Some medicines can reduce vitamin D absorption or change vitamin D metabolism.",
            recommendation:
              "Ask whether your dose or blood-level monitoring should change.",
          },
    );
  }

  if (id === "supplement-prenatal") {
    interactions.push(
      fr
        ? {
            with: "Lévothyroxine",
            severity: "caution",
            concern:
              "Les prénatales contiennent souvent du fer, du calcium, du magnésium ou du zinc, qui peuvent se lier à la lévothyroxine et réduire l'absorption de l'hormone thyroïdienne.",
            recommendation:
              "Prends la lévothyroxine à jeun et espace la prénatale d'au moins 4 heures, sauf indication contraire de ta soignante.",
            linkedBlockId: "med-levothyroxine",
          }
        : {
            with: "Levothyroxine",
            severity: "caution",
            concern:
              "Prenatals often contain iron, calcium, magnesium, or zinc, which can bind levothyroxine and reduce thyroid hormone absorption.",
            recommendation:
              "Take levothyroxine on an empty stomach and separate the prenatal by at least 4 hours unless your clinician gives different instructions.",
            linkedBlockId: "med-levothyroxine",
          },
      fr
        ? {
            with: "Doxycycline ou autres antibiotiques de la famille des tétracyclines ou fluoroquinolones",
            severity: "caution",
            concern:
              "Les minéraux de la prénatale peuvent se lier à ces antibiotiques et les rendre moins efficaces.",
            recommendation:
              "Espace la prénatale de l'antibiotique selon les indications de ta soignante, souvent d'au moins 2 heures.",
            linkedBlockId: "med-doxycycline",
          }
        : {
            with: "Doxycycline or other tetracycline/quinolone antibiotics",
            severity: "caution",
            concern:
              "Prenatal minerals can bind these antibiotics and make them less effective.",
            recommendation:
              "Separate the prenatal from the antibiotic by the timing your prescriber recommends, often at least 2 hours.",
            linkedBlockId: "med-doxycycline",
          },
      fr
        ? {
            with: "Fer, iode, vitamine D, folate, choline, oméga-3 ou complexe B en plus",
            severity: "note",
            concern:
              "Une prénatale est déjà un produit multi-ingrédients, donc empiler des compléments isolés peut accidentellement doubler la dose de nutriments clés.",
            recommendation:
              "Vérifie l'étiquette complète avant d'ajouter d'autres compléments de fertilité, surtout fer, iode, folate, vitamine D et choline.",
            linkedBlockId: "supplement-methylfolate",
          }
        : {
            with: "Separate iron, iodine, vitamin D, folate, choline, omega-3, or B-complex supplements",
            severity: "note",
            concern:
              "A prenatal is already a multi-ingredient product, so stacking standalone supplements can accidentally double-dose key nutrients.",
            recommendation:
              "Check the full label before adding separate fertility supplements, especially iron, iodine, folate, vitamin D, and choline.",
            linkedBlockId: "supplement-methylfolate",
          },
    );
  }

  if (
    id === "supplement-methylfolate" ||
    id === "supplement-vitamin-b-complex"
  ) {
    interactions.push(
      fr
        ? {
            with: "Antiépileptiques, méthotrexate ou autres médicaments agissant sur la voie des folates",
            severity: "caution",
            concern:
              "Le dosage en folate et en vitamines B peut être important quand des médicaments agissent via les folates ou les voies neurologiques.",
            recommendation:
              "Demande à la prescriptrice avant un méthylfolate ou un complexe B à forte dose si tu prends un antiépileptique, du méthotrexate ou une chimiothérapie.",
          }
        : {
            with: "Anti-seizure medicines, methotrexate, or other folate-pathway medicines",
            severity: "caution",
            concern:
              "Folate and B-vitamin dosing can matter when medicines work through folate or neurologic pathways.",
            recommendation:
              "Ask the prescribing clinician before high-dose methylfolate or B-complex if you take anti-seizure medication, methotrexate, or chemotherapy.",
          },
    );
  }

  if (id === "supplement-choline") {
    interactions.push(
      fr
        ? {
            with: "Médicaments anticholinergiques ou produits de choline à très forte dose",
            severity: "note",
            concern:
              "Un apport total élevé en choline peut provoquer troubles digestifs, sueurs ou odeur de poisson; les anticholinergiques peuvent compliquer l'interprétation des symptômes.",
            recommendation:
              "Compte ensemble la choline de l'alimentation, de la prénatale et des compléments isolés avant d'augmenter la dose.",
          }
        : {
            with: "Anticholinergic medicines or very high-dose choline products",
            severity: "note",
            concern:
              "High total choline intake can cause GI symptoms, sweating, or fishy odor; anticholinergic medicines may complicate symptom interpretation.",
            recommendation:
              "Count food, prenatal, and standalone choline together before increasing the dose.",
          },
    );
  }

  if (id === "supplement-iodine") {
    interactions.push(
      fr
        ? {
            with: "Lévothyroxine, antithyroïdiens, lithium, amiodarone ou maladie thyroïdienne",
            severity: "caution",
            concern:
              "L'iode modifie la production d'hormones thyroïdiennes; trop peu comme trop peuvent déstabiliser la prise en charge thyroïdienne.",
            recommendation:
              "Utilise une dose d'iode prévisible de type prénatale et évite le kelp à forte dose, sauf indication de ta soignante.",
            linkedBlockId: "med-levothyroxine",
          }
        : {
            with: "Levothyroxine, antithyroid drugs, lithium, amiodarone, or thyroid disease",
            severity: "caution",
            concern:
              "Iodine changes thyroid hormone production; both too little and too much can destabilize thyroid management.",
            recommendation:
              "Use predictable prenatal-dose iodine and avoid high-dose kelp unless your clinician recommends it.",
            linkedBlockId: "med-levothyroxine",
          },
    );
  }

  if (id === "supplement-selenium") {
    interactions.push(
      fr
        ? {
            with: "Autres sources de sélénium ou noix du Brésil",
            severity: "caution",
            concern:
              "Le sélénium a une marge de sécurité étroite et plusieurs sources peuvent faire dépasser la plage sûre.",
            recommendation:
              "Évite de cumuler compléments de sélénium et consommation importante de noix du Brésil.",
          }
        : {
            with: "Other selenium sources or Brazil nuts",
            severity: "caution",
            concern:
              "Selenium has a narrow safety window and duplicate sources can exceed the safe range.",
            recommendation:
              "Avoid stacking selenium supplements and high Brazil-nut intake.",
          },
    );
  }

  if (id === "supplement-vitamin-c") {
    interactions.push(
      fr
        ? {
            with: "Compléments de fer ou états de surcharge en fer",
            severity: "note",
            concern:
              "La vitamine C peut augmenter l'absorption du fer non héminique, ce qui est utile en cas de carence mais pas en cas de surcharge.",
            recommendation:
              "Associe au fer de façon intentionnelle uniquement en cas de ferritine basse ou selon les indications de ta soignante.",
          }
        : {
            with: "Iron supplements or iron overload conditions",
            severity: "note",
            concern:
              "Vitamin C can increase non-heme iron absorption, which is useful for deficiency but not for iron overload.",
            recommendation:
              "Pair intentionally with iron only when treating low ferritin or as your clinician directs.",
          },
    );
  }

  if (id === "supplement-nac") {
    interactions.push(
      fr
        ? {
            with: "Nitroglycérine ou antihypertenseurs",
            severity: "caution",
            concern:
              "La NAC peut renforcer les effets vasodilatateurs et contribuer à maux de tête, bouffées de chaleur ou hypotension.",
            recommendation:
              "Demande à une soignante avant de combiner avec des dérivés nitrés ou des antihypertenseurs.",
          }
        : {
            with: "Nitroglycerin or blood-pressure medication",
            severity: "caution",
            concern:
              "NAC can enhance vasodilating effects and may contribute to headache, flushing, or low blood pressure.",
            recommendation:
              "Ask a clinician before combining with nitrates or blood-pressure medication.",
          },
    );
  }

  if (id === "supplement-melatonin") {
    interactions.push(
      fr
        ? {
            with: "Sédatifs, alcool, anticoagulants, traitements du diabète ou immunosuppresseurs",
            severity: "caution",
            concern:
              "La mélatonine peut ajouter de la sédation et influencer les saignements, la glycémie ou la signalisation immunitaire chez certaines personnes.",
            recommendation:
              "Confirme le timing avec ton centre, surtout avant un transfert, une grossesse ou une intervention.",
          }
        : {
            with: "Sedatives, alcohol, anticoagulants, diabetes medicines, or immune-suppressing medicines",
            severity: "caution",
            concern:
              "Melatonin can add sedation and may affect bleeding, glucose, or immune signaling in susceptible patients.",
            recommendation:
              "Confirm timing with your clinic, especially before transfer, pregnancy, or procedures.",
          },
    );
  }

  if (id === "supplement-probiotics") {
    interactions.push(
      fr
        ? {
            with: "Antibiotiques, immunosuppression sévère ou cathéters centraux",
            severity: "caution",
            concern:
              "Les antibiotiques peuvent réduire la survie des probiotiques; de rares infections sont à craindre chez les patientes sévèrement immunodéprimées.",
            recommendation:
              "Espace des antibiotiques comme indiqué et évite de commencer toi-même si tu es nettement immunodéprimée.",
          }
        : {
            with: "Antibiotics, severe immune suppression, or central lines",
            severity: "caution",
            concern:
              "Antibiotics can reduce probiotic survival; rare infections are a concern in severely immunocompromised patients.",
            recommendation:
              "Separate from antibiotics as directed and avoid self-starting if you are significantly immunosuppressed.",
          },
    );
  }

  if (id === "supplement-ashwagandha") {
    interactions.push(
      fr
        ? {
            with: "Traitement thyroïdien, sédatifs, alcool ou immunosuppresseurs",
            severity: "caution",
            concern:
              "L'ashwagandha peut influencer la sédation, les marqueurs thyroïdiens et la signalisation immunitaire chez certaines personnes.",
            recommendation:
              "À éviter pendant la grossesse et confirme le moment de l'arrêt avant un transfert ou un cycle médicamenté.",
          }
        : {
            with: "Thyroid medication, sedatives, alcohol, or immune-suppressing medicines",
            severity: "caution",
            concern:
              "Ashwagandha can affect sedation, thyroid markers, and immune signaling in some people.",
            recommendation:
              "Avoid during pregnancy and confirm stop timing before transfer or medicated cycles.",
          },
    );
  }

  if (id === "supplement-l-arginine") {
    interactions.push(
      fr
        ? {
            with: "Dérivés nitrés, sildénafil/inhibiteurs de PDE5, antihypertenseurs ou anticoagulants",
            severity: "caution",
            concern:
              "La L-arginine peut influencer les voies de l'oxyde nitrique, la tension artérielle et la tendance aux saignements.",
            recommendation:
              "Demande avant de combiner avec sildénafil, dérivés nitrés, antihypertenseurs ou protocoles aspirine/HBPM.",
            linkedBlockId: "med-sildenafil",
          }
        : {
            with: "Nitrates, sildenafil/PDE5 inhibitors, blood-pressure medicines, or anticoagulants",
            severity: "caution",
            concern:
              "L-arginine can affect nitric-oxide pathways, blood pressure, and bleeding tendency.",
            recommendation:
              "Ask before combining with sildenafil, nitrates, antihypertensives, or aspirin/LMWH protocols.",
            linkedBlockId: "med-sildenafil",
          },
    );
  }

  if (id === "supplement-l-carnitine") {
    interactions.push(
      fr
        ? {
            with: "Warfarine ou risque convulsif",
            severity: "caution",
            concern:
              "La L-carnitine a des cas rapportés de modification du contrôle de l'anticoagulation et peut être pertinente chez les personnes ayant des antécédents de convulsions.",
            recommendation:
              "Préviens ta soignante avant de commencer si tu prends de la warfarine/des anticoagulants ou si tu as une épilepsie.",
            linkedBlockId: "med-warfarin",
          }
        : {
            with: "Warfarin or seizure-threshold concerns",
            severity: "caution",
            concern:
              "L-carnitine has case reports of changing anticoagulation control and may be relevant for people with seizure history.",
            recommendation:
              "Tell your clinician before starting if you take warfarin/anticoagulants or have a seizure disorder.",
            linkedBlockId: "med-warfarin",
          },
    );
  }

  if (id === "supplement-creatine") {
    interactions.push(
      fr
        ? {
            with: "Médicaments à risque rénal, diurétiques ou situations à risque de déshydratation",
            severity: "note",
            concern:
              "La créatine est généralement bien tolérée, mais la surveillance rénale et l'hydratation comptent chez les patientes à risque.",
            recommendation:
              "Préviens ta soignante en cas de maladie rénale, de prise de médicaments néphrotoxiques ou de restriction hydrique.",
          }
        : {
            with: "Kidney-risk medicines, diuretics, or dehydration-prone situations",
            severity: "note",
            concern:
              "Creatine is usually well tolerated, but kidney monitoring and hydration matter in higher-risk patients.",
            recommendation:
              "Tell your clinician if you have kidney disease, use nephrotoxic medicines, or are on fluid restriction.",
          },
    );
  }

  if (id === "supplement-maca") {
    interactions.push(
      fr
        ? {
            with: "Hormonothérapie, maladie thyroïdienne ou stimulation de fertilité en cours",
            severity: "note",
            concern:
              "La maca est vendue pour les hormones, mais ses effets endocriniens ne sont pas assez prévisibles pour s'ajouter sans réflexion à un traitement.",
            recommendation:
              "Considère-la comme une option non centrale et signale-la avant les cycles monitorés.",
          }
        : {
            with: "Hormone therapy, thyroid disease, or active fertility stimulation",
            severity: "note",
            concern:
              "Maca is marketed for hormones, but endocrine effects are not predictable enough to layer casually onto treatment.",
            recommendation:
              "Treat as optional, non-core use and disclose it before monitored cycles.",
          },
    );
  }

  if (id === "supplement-vitex") {
    interactions.push(
      fr
        ? {
            with: "Agonistes/antagonistes dopaminergiques, contraception hormonale, progestérone ou médicaments de fertilité",
            severity: "caution",
            concern:
              "Le gattilier peut influencer la signalisation dopamine/prolactine et brouiller le suivi d'un protocole hormonal.",
            recommendation:
              "Ne l'ajoute pas seule pendant les cycles médicamentés ou pendant l'évaluation de la prolactine, de l'ovulation ou de la phase lutéale.",
          }
        : {
            with: "Dopamine agonists/antagonists, hormonal contraception, progesterone, or fertility medications",
            severity: "caution",
            concern:
              "Vitex may affect dopamine/prolactin signaling and can muddy hormone-protocol monitoring.",
            recommendation:
              "Do not self-add during medicated cycles or while evaluating prolactin, ovulation, or luteal-phase concerns.",
          },
    );
  }

  if (id === "supplement-dhea") {
    interactions.push(
      fr
        ? {
            with: "Hormonothérapie, PMOS (ex SOPK), états d'hyperandrogénie, médicaments modifiant la testostérone ou pathologies hormonosensibles",
            severity: "critical",
            concern:
              "La DHEA est un précurseur hormonal et peut augmenter l'exposition aux androgènes ou aux œstrogènes.",
            recommendation:
              "À utiliser uniquement avec un suivi clinique de la DHEA-S/testostérone et un diagnostic clair comme une réserve ovarienne diminuée.",
            linkedBlockId: "supplement-dhea",
          }
        : {
            with: "Hormone therapy, PMOS (ex PCOS), high-androgen states, testosterone-modifying medicines, or hormone-sensitive conditions",
            severity: "critical",
            concern:
              "DHEA is a hormone precursor and can raise androgen or estrogen exposure.",
            recommendation:
              "Use only with clinician monitoring of DHEA-S/testosterone and a clear diagnosis such as diminished ovarian reserve.",
            linkedBlockId: "supplement-dhea",
          },
    );
  }

  if (id === "supplement-royal-jelly") {
    interactions.push(
      fr
        ? {
            with: "Allergie aux produits de la ruche, asthme ou traitements antiallergiques",
            severity: "critical",
            concern:
              "La gelée royale peut déclencher des réactions allergiques sévères chez les personnes prédisposées.",
            recommendation:
              "À éviter en cas d'allergie aux produits de la ruche ou d'asthme à composante allergique, sauf accord d'une soignante.",
          }
        : {
            with: "Bee-product allergy, asthma, or allergy medications",
            severity: "critical",
            concern:
              "Royal jelly can trigger serious allergic reactions in susceptible people.",
            recommendation:
              "Avoid if you have bee-product allergy or asthma-prone allergic reactions unless cleared by a clinician.",
          },
    );
  }

  if (id === "supplement-shatavari") {
    interactions.push(
      fr
        ? {
            with: "Œstrogénothérapie, hormones de fertilité, diurétiques, lithium ou pathologies œstrogéno-sensibles",
            severity: "caution",
            concern:
              "Les produits à base de shatavari varient et peuvent avoir des effets hormonaux ou diurétiques.",
            recommendation:
              "Ne l'ajoute pas à un protocole hormonal sans que ton centre l'ait revu.",
          }
        : {
            with: "Estrogen therapy, fertility hormones, diuretics, lithium, or estrogen-sensitive conditions",
            severity: "caution",
            concern:
              "Shatavari products vary and may have hormone-like or diuretic effects.",
            recommendation:
              "Do not layer onto hormone protocols without your clinic reviewing it.",
          },
    );
  }

  if (id === "supplement-nad-nmn") {
    interactions.push(
      fr
        ? {
            with: "Traitement anticancéreux, thérapie immunomodulatrice ou grossesse",
            severity: "note",
            concern:
              "La sécurité chez l'humain en fertilité et l'exposition des tissus reproducteurs aux précurseurs du NAD+ sont mal définies.",
            recommendation:
              "À éviter pendant la grossesse sauf recommandation spécifique, et signale ton usage si tu es suivie en oncologie ou en immunologie.",
          }
        : {
            with: "Cancer treatment, immune-modulating therapy, or pregnancy",
            severity: "note",
            concern:
              "Human fertility safety and reproductive-tissue exposure for NAD+ precursors are not well defined.",
            recommendation:
              "Avoid during pregnancy unless specifically recommended, and disclose use if you are under oncology or immune care.",
          },
    );
  }

  if (id === "supplement-collagen") {
    interactions.push(
      fr
        ? {
            with: "Maladie rénale, restriction protéique ou allergies à la source du produit",
            severity: "note",
            concern:
              "Le collagène est une protéine plutôt qu'un médicament agissant sur la fertilité, mais les allergènes de la source et les limites en protéines peuvent compter.",
            recommendation:
              "Vérifie les ingrédients sources et ne remplace pas un apport protéique équilibré par du collagène seul.",
          }
        : {
            with: "Kidney disease, protein restriction, or product-source allergies",
            severity: "note",
            concern:
              "Collagen is a protein product rather than a fertility-active drug, but source allergens and protein limits can matter.",
            recommendation:
              "Check source ingredients and avoid replacing balanced protein intake with collagen alone.",
          },
    );
  }

  if (
    title.includes("calcium") ||
    title.includes("iron") ||
    title.includes("magnesium") ||
    title.includes("zinc")
  ) {
    interactions.push(...mineralBindingInteractions(locale));
  }

  return interactions;
}

export function withInteractionPresets(
  block: ContentBlock,
  locale: string = "en",
): ContentBlock {
  if (block.kind === "medication") {
    const medicationBlock = block as ContentBlock<"medication">;
    const inferred = inferMedicationInteractions(medicationBlock, locale);

    return {
      ...medicationBlock,
      body: {
        ...medicationBlock.body,
        interactions: mergeInteractions(
          medicationBlock.body.interactions,
          inferred,
        ),
      },
    };
  }

  if (block.kind === "supplement") {
    const supplementBlock = block as ContentBlock<"supplement">;
    const inferred = inferSupplementInteractions(supplementBlock, locale);

    return {
      ...supplementBlock,
      body: {
        ...supplementBlock.body,
        interactions: mergeInteractions(
          supplementBlock.body.interactions,
          inferred,
        ),
      },
    };
  }

  return block;
}
